import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useAuth } from '../../contexts/AuthContext';
import { readStoredFlowSession } from '../../features/flow/session';
import {
  CheckCircle,
  Circle,
  Clock,
  Coins,
  Globe,
  HeadsetHelp,
  HomeAltSlim,
  Mail,
  NavArrowDown,
  NavArrowUp,
  Page,
  PageSearch,
  SidebarCollapse,
  SidebarExpand,
  StatDown,
  Wrench,
} from 'iconoir-react';
import {
  resolveSectionId,
  SIDEBAR_SECTIONS,
  type SectionId,
  type SidebarSectionConfig,
} from '../../features/flow/sidebarSections';
import { cleanTitle } from '../../features/flow/utils';

const TEAL = '#07A2A0';

const iconClass = 'w-[24px] h-[24px] shrink-0 text-[#323334]';

const SECTION_ICONS: Record<SectionId, React.ReactNode> = {
  start: <HomeAltSlim className={iconClass} />,
  'my-information': <Page className={iconClass} />,
  property: <Globe className={iconClass} />,
  income: <Coins className={iconClass} />,
  deductions: <StatDown className={iconClass} />,
  'foreign-reporting': <Globe className={iconClass} />,
  summary: <PageSearch className={iconClass} />,
  submission: <Mail className={iconClass} />,
};

export interface FlowScreenListItem {
  screen_id: string;
  title: string;
  screen_type: string;
  category: string;
  country: string;
  order: number;
}

type SubItemStatus = 'pending' | 'in_progress' | 'completed';

function SubStatusIcon({ status }: { status: SubItemStatus }) {
  if (status === 'completed') {
    return <CheckCircle className="w-5 h-5 shrink-0" style={{ color: TEAL }} />;
  }
  if (status === 'in_progress') {
    return <Clock className="w-5 h-5 shrink-0 text-gray-700" />;
  }
  return <Circle className="w-5 h-5 shrink-0 text-gray-400" />;
}

function subItemStatus(
  screenId: string,
  currentScreenId: string | null,
  visitedScreenIds: string[]
): SubItemStatus {
  if (currentScreenId && screenId === currentScreenId) return 'in_progress';
  if (visitedScreenIds.includes(screenId)) return 'completed';
  return 'pending';
}

export interface QuestionnaireNavSidebarProps {
  screens: FlowScreenListItem[];
  currentScreenId: string | null;
  visitedScreenIds: string[];
  /** Sidebar structure; defaults to `SIDEBAR_SECTIONS`. */
  sections?: SidebarSectionConfig[];
  /** Desktop / tablet rail width: narrow icon strip vs full. Toggled from the sidebar header. */
  desktopCollapsed: boolean;
  onDesktopCollapsedChange: (collapsed: boolean) => void;
  /**
   * Mobile (&lt; md): when false, sidebar renders nothing (no rail).
   * When true, sidebar is a full-screen overlay.
   */
  mobileDrawerOpen?: boolean;
  onMobileDrawerClose?: () => void;
  /** Navigate to a screen already visited (sidebar); unvisited screens stay non-interactive. */
  onNavigateToScreen?: (screenId: string) => void;
}

export const QuestionnaireNavSidebar: React.FC<QuestionnaireNavSidebarProps> = ({
  screens,
  currentScreenId,
  visitedScreenIds,
  sections = SIDEBAR_SECTIONS,
  desktopCollapsed: collapsed,
  onDesktopCollapsedChange: setCollapsed,
  mobileDrawerOpen = false,
  onMobileDrawerClose,
  onNavigateToScreen,
}) => {
  const { t } = useTranslation('common');
  const { t: tScreenTitle } = useTranslation('screens');
  const { isAuthenticated, userId } = useAuth();
  const { sessionId: clientSessionId } = readStoredFlowSession('identified', userId);
  const { sessionId: anonSessionId } = readStoredFlowSession('anonymous');
  const homeRoute = (clientSessionId && isAuthenticated)
    ? '/client'
    : anonSessionId
    ? '/new'
    : isAuthenticated ? '/client' : '/new';
  const [expanded, setExpanded] = useState<Set<SectionId>>(() => new Set());
  /** Mobile drawer: slide + leave animation (JS); layout breakpoints use `md:` / `max-md:` */
  const [mobileDrawerSlideIn, setMobileDrawerSlideIn] = useState(false);
  const [mobileLeaveAnimating, setMobileLeaveAnimating] = useState(false);

  const drawerActive = mobileDrawerOpen || mobileLeaveAnimating;

  useEffect(() => {
    if (!mobileDrawerOpen) return;
    setMobileLeaveAnimating(false);
    setMobileDrawerSlideIn(false);
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => setMobileDrawerSlideIn(true));
    });
    return () => cancelAnimationFrame(id);
  }, [mobileDrawerOpen]);

  /** Clear mobile-only animation state when crossing to desktop mid-gesture */
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onCrossMd = () => {
      if (mq.matches) {
        setMobileLeaveAnimating(false);
        setMobileDrawerSlideIn(false);
      }
    };
    mq.addEventListener('change', onCrossMd);
    return () => mq.removeEventListener('change', onCrossMd);
  }, []);

  const screensBySection = useMemo(() => {
    const map = new Map<SectionId, FlowScreenListItem[]>();
    for (const s of sections) {
      map.set(s.id, []);
    }
    const sorted = [...screens].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    for (const sc of sorted) {
      // Some screens need bespoke placement beyond broad category matching.
      const sid =
        sc.screen_id === 'housing_situation'
          ? 'my-information'
          : sc.screen_id === 'income_review'
            ? 'income'
            : resolveSectionId(sc.category, sections);
      map.get(sid)!.push(sc);
    }
    return map;
  }, [screens, sections]);

  const toggleSection = (id: SectionId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    if (!currentScreenId) return;
    for (const sec of sections) {
      const list = screensBySection.get(sec.id) ?? [];
      if (list.some((s) => s.screen_id === currentScreenId)) {
        setExpanded((prev) => new Set(prev).add(sec.id));
        break;
      }
    }
  }, [currentScreenId, screensBySection, sections]);

  const beginMobileDrawerClose = () => {
    if (mobileLeaveAnimating) return;
    setMobileLeaveAnimating(true);
    setMobileDrawerSlideIn(false);
  };

  const onMobilePanelTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.propertyName !== 'transform') return;
    if (!mobileLeaveAnimating) return;
    setMobileLeaveAnimating(false);
    onMobileDrawerClose?.();
  };

  const aside = (
    <aside
      className={clsx(
        'flex flex-col min-h-0 bg-white',
        drawerActive ? 'max-md:flex' : 'max-md:hidden',
        'md:relative md:flex md:h-full md:min-h-0 md:shrink-0 md:flex-none md:translate-x-0 md:border-r md:border-gray-200',
        drawerActive &&
          'max-md:fixed max-md:inset-0 max-md:z-50 max-md:h-full max-md:w-full max-md:max-w-none max-md:border-0',
        'max-md:transition-transform max-md:duration-300 max-md:ease-out max-md:will-change-transform',
        mobileDrawerSlideIn && !mobileLeaveAnimating ? 'max-md:translate-x-0' : 'max-md:-translate-x-full',
        collapsed ? 'md:w-14 md:min-w-[56px] md:max-w-[56px]' : 'md:w-[262px] md:min-w-[262px] md:max-w-[262px]'
      )}
      onTransitionEnd={onMobilePanelTransitionEnd}
    >
      <div
        className={clsx(
          'flex w-full h-[64px] items-center gap-2 border-b border-[#E2E8F0] px-2',
          collapsed && 'md:justify-center'
        )}
      >
        <div
          className={clsx(
            'flex h-[64px] min-w-0 flex-[1_0_0] items-center gap-2 p-2',
            collapsed && 'md:hidden'
          )}
        >
          <Link to={homeRoute} className="cursor-pointer">
            <img
              src="/img/innotaxes-logo.png"
              alt="InnoTaxes"
              width={126}
              height={32}
              className="aspect-[63/16] h-[32px] w-[126px] object-contain object-left shrink-0"
            />
          </Link>
        </div>
        <button
          type="button"
          className="md:hidden shrink-0 rounded-lg p-2 text-gray-600 hover:bg-gray-100"
          aria-label={t('sidebar.close_navigation')}
          onClick={beginMobileDrawerClose}
        >
          <SidebarCollapse className="h-[24px] w-[24px] text-[#323334]" />
        </button>
        <button
          type="button"
          className="hidden md:flex shrink-0 rounded-lg p-2 text-gray-600 hover:bg-gray-100"
          aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          onClick={() => setCollapsed(!collapsed)}
        >
          {collapsed ? (
            <SidebarExpand className="h-[24px] w-[24px] text-[#323334]" />
          ) : (
            <SidebarCollapse className="h-[24px] w-[24px] text-[#323334]" />
          )}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto min-w-0">
        {sections.map((sec) => {
          const items = screensBySection.get(sec.id) ?? [];
          const isOpen = expanded.has(sec.id);
          const hasSubs = items.length > 0;

          return (
            <div key={sec.id} className="flex flex-col align-center self-stretch gap-2 p-2">
              <button
                type="button"
                onClick={() => hasSubs && toggleSection(sec.id)}
                className={clsx(
                  'w-full flex items-center gap-2 p-2 rounded-xl text-left transition-colors',
                  isOpen && hasSubs ? 'bg-[#e6f7f7]' : 'hover:bg-gray-50',
                  !hasSubs && 'opacity-60 cursor-default'
                )}
              >
                <span className="shrink-0 flex items-center justify-center w-[22px]">{SECTION_ICONS[sec.id]}</span>
                <span
                  className={clsx(
                    'flex flex-1 min-w-0 items-center gap-2',
                    collapsed && 'md:hidden'
                  )}
                >
                  <span className="flex-1 text-left text-[16px] font-medium text-[#525353]">{t(`sidebar.sections.${sec.id}`, { defaultValue: sec.label })}</span>
                  {hasSubs &&
                    (isOpen ? (
                      <NavArrowUp className="w-[18px] h-[18px] text-[#323334] shrink-0" />
                    ) : (
                      <NavArrowDown className="w-[18px] h-[18px] text-[#323334] shrink-0" />
                    ))}
                </span>
              </button>

              {isOpen && hasSubs && (
                <div
                  className={clsx(
                    'mx-2 mt-1 mb-2 p-2 rounded-xl border border-gray-200 bg-white',
                    collapsed && 'md:hidden'
                  )}
                >
                  <ul className="space-y-0.5">
                    {items.map((item) => {
                      const st = subItemStatus(item.screen_id, currentScreenId, visitedScreenIds);
                      const visited = visitedScreenIds.includes(item.screen_id);
                      const isCurrent = currentScreenId === item.screen_id;
                      const canJump =
                        Boolean(onNavigateToScreen) && visited && !isCurrent;
                      const fallbackLabel = cleanTitle(item.title) || item.screen_id;
                      // Locale `screens.json` titles often repeat "Welcome & Setup …"; strip after i18n too.
                      const label =
                        cleanTitle(
                          tScreenTitle(`${item.screen_id}.title`, {
                            defaultValue: fallbackLabel,
                          })
                        ) || item.screen_id;
                      return (
                        <li key={item.screen_id}>
                          {canJump ? (
                            <button
                              type="button"
                              className={clsx(
                                'w-full text-left flex items-center gap-2 px-2 py-2 rounded-lg text-sm',
                                'text-[#131315] hover:bg-gray-50 cursor-pointer'
                              )}
                              onClick={() => {
                                beginMobileDrawerClose();
                                onNavigateToScreen!(item.screen_id);
                              }}
                            >
                              <SubStatusIcon status={st} />
                              <span className="leading-snug">{label}</span>
                            </button>
                          ) : (
                            <div
                              className={clsx(
                                'flex items-center gap-2 px-2 py-2 rounded-lg text-sm',
                                visited ? 'text-gray-600' : 'text-gray-400',
                                st === 'in_progress' && 'bg-gray-50'
                              )}
                              aria-disabled={!visited}
                            >
                              <SubStatusIcon status={st} />
                              <span className="leading-snug">{label}</span>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div>
        <div className="p-2">
          <button
            type="button"
            className="w-full flex items-center gap-2 p-2 rounded-xl text-left text-gray-700 hover:bg-gray-50"
          >
            <HeadsetHelp className={iconClass} />
            <span className={clsx('text-[16px]', collapsed && 'md:hidden')}>{t('sidebar.support')}</span>
          </button>
        </div>
        <div className="p-2">
          <button
          type="button"
          className="w-full flex items-center gap-2 p-2 rounded-xl text-left text-gray-700 hover:bg-gray-50"
        >
          <Wrench className={iconClass} />
            <span className={clsx('text-[16px]', collapsed && 'md:hidden')}>{t('sidebar.profile')}</span>
          </button>
        </div>
      </div>
    </aside>
  );

  const backdropOpaque = mobileDrawerSlideIn && !mobileLeaveAnimating;

  return (
    <>
      {drawerActive ? (
        <button
          type="button"
          className={clsx(
            'fixed inset-0 z-40 bg-black/40 md:hidden transition-opacity duration-300 ease-out',
            backdropOpaque ? 'opacity-100' : 'opacity-0 pointer-events-none'
          )}
          onClick={beginMobileDrawerClose}
          aria-label={t('sidebar.close_overlay')}
        />
      ) : null}
      {aside}
    </>
  );
};

export default QuestionnaireNavSidebar;
